/**
 * Restore wallet page — composition of MnemonicEntry and PinSetup.
 *
 * No business logic here (per CLAUDE.md rule). All orchestration
 * is handled by useRestoreWallet hook.
 *
 * Flow: Enter 12 words -> validate -> sync -> set new PIN -> restore tokens -> complete.
 *
 * Tier 1 - wallet restore flow.
 *
 * @module RestorePage
 */

import { useEffect, type ReactNode } from 'react';
import { MnemonicEntry } from '@/components/ui/mnemonic-entry';
import { useRestoreWallet } from '../hooks/useRestoreWallet';
import { Button } from '@/components/ui/button';
import { RestoreProgress } from '@/components/RestoreProgress';
import { OnboardingProgressBar } from '../components/OnboardingProgressBar';

/** Props for RestorePage. */
interface RestorePageProps {
  /** Called when restore is fully complete (PIN set, mnemonic stored). */
  readonly onComplete: () => void;
  /** Called when user wants to go back to welcome screen. */
  readonly onBack: () => void;
  /** Function to restore Evolu with a BIP-85 child mnemonic. */
  readonly restoreEvolu: (childMnemonic: string) => Promise<void>;
  /** Optional PIN setup component to render. Receives onComplete callback. */
  readonly renderPinSetup?: (onPinSet: (pin: string) => void) => ReactNode;
}

/**
 * Page component for the wallet restore flow.
 *
 * Composes MnemonicEntry for word input and a PIN setup step.
 * Uses useRestoreWallet hook for all state management.
 * The Evolu restore function is injected via props for testability.
 *
 * Tier 1 - wallet restore flow.
 */
export default function RestorePage({
  onComplete,
  onBack,
  restoreEvolu,
  renderPinSetup,
}: RestorePageProps): ReactNode {
  const {
    state,
    error,
    syncSlow,
    tokenRestoreProgress,
    startRestore,
    setPinAndFinalize,
    skipSync,
  } = useRestoreWallet(restoreEvolu);

  // Notify parent when restore flow reaches 'complete' state
  useEffect(() => {
    if (state === 'complete') {
      onComplete();
    }
  }, [state, onComplete]);

  /** Total user-visible steps in the restore flow. */
  const RESTORE_TOTAL_STEPS = 4;

  /** Map restore state to user-visible step number. */
  const stepForState = (): { step: number; name: string } => {
    switch (state) {
      case 'idle':
      case 'validating':
        return { step: 1, name: 'Enter recovery phrase' };
      case 'syncing':
        return { step: 2, name: 'Syncing wallet' };
      case 'pin_setup':
        return { step: 3, name: 'Set up PIN' };
      case 'encrypting':
      case 'restoring_tokens':
      case 'complete':
        return { step: 4, name: 'Restoring wallet' };
    }
  };

  const { step: currentStep, name: stepName } = stepForState();

  return (
    <main className="flex min-h-screen flex-col items-center justify-center p-6">
      <div className="w-full max-w-md">
        <OnboardingProgressBar currentStep={currentStep} totalSteps={RESTORE_TOTAL_STEPS} stepName={stepName} />

        {state === 'idle' || state === 'validating' ? (
          <>
            <h1 className="mb-2 text-center text-2xl font-bold tracking-tight">Restore Your Wallet</h1>
            <p className="mb-8 text-center text-sm text-muted-foreground">
              Enter your 12-word recovery phrase to restore your wallet.
            </p>
            <MnemonicEntry
              onComplete={startRestore}
              error={error ?? undefined}
              loading={state === 'validating'}
            />
            <button
              type="button"
              onClick={onBack}
              className="mt-4 w-full text-center text-sm text-muted-foreground hover:text-foreground"
            >
              Back
            </button>
          </>
        ) : null}

        {state === 'syncing' ? (
          <div className="flex flex-col items-center gap-4">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            <p className="text-sm text-muted-foreground">
              {syncSlow
                ? 'Syncing is taking a moment. Your data will catch up.'
                : 'Syncing your wallet data...'}
            </p>
            {syncSlow ? (
              <Button variant="outline" size="sm" onClick={skipSync}>
                Continue without waiting
              </Button>
            ) : null}
          </div>
        ) : null}

        {state === 'pin_setup' ? (
          <div className="flex flex-col gap-4">
            <p className="text-center text-sm text-muted-foreground">
              Set a new PIN to secure your wallet on this device.
            </p>
            {renderPinSetup ? (
              renderPinSetup(setPinAndFinalize)
            ) : (
              <p className="text-center text-xs text-muted-foreground">
                PIN setup component loading...
              </p>
            )}
          </div>
        ) : null}

        {state === 'encrypting' ? (
          <div className="flex flex-col items-center gap-4">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            <p className="text-sm text-muted-foreground">Securing your wallet...</p>
          </div>
        ) : null}

        {state === 'restoring_tokens' ? (
          <RestoreProgress progress={tokenRestoreProgress} isComplete={false} totalSats={0} />
        ) : null}

        {error && state !== 'idle' && state !== 'validating' ? (
          <p className="mt-4 text-center text-sm text-destructive" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </main>
  );
}
